// The two buttons that change whether someone may apply to drive again.
//
// "Do not allow to interview again" is a manager's permanent decision and
// needs a reason; "Lift block" is an admin undoing a no show, No thanks or Do
// not interview block, also with a reason. Both are decided and re-checked in
// Postgres (migration 0278). These buttons only collect the sentence and show
// what the database said.
import { useState, type ReactNode } from 'react'
import { Ban, Unlock } from 'lucide-react'
import { useBlockFromInterviewing, useLiftApplicantBlock } from '../../hooks/backend/drivers'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../../lib/shadcn/dialog'
import { Button } from '../../lib/shadcn/button'
import { Textarea } from '../../lib/shadcn/textarea'
import { toast } from '../../lib/shadcn/sonner'
import { writeErrorMessage } from '../../utils/writeError'

function ReasonDialog({
  trigger,
  title,
  description,
  placeholder,
  confirmLabel,
  onConfirm,
}: {
  trigger: ReactNode
  title: string
  description: string
  placeholder: string
  confirmLabel: string
  onConfirm: (reason: string) => Promise<boolean>
}) {
  const [open, setOpen] = useState(false)
  const [reason, setReason] = useState('')
  const [busy, setBusy] = useState(false)
  const ready = reason.trim().length > 0

  async function confirm() {
    if (!ready) return
    setBusy(true)
    const done = await onConfirm(reason.trim())
    setBusy(false)
    if (done) {
      setOpen(false)
      setReason('')
    }
  }

  return (
    <Dialog open={open} onOpenChange={(v) => !busy && setOpen(v)}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        <Textarea
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder={placeholder}
          rows={3}
          autoFocus
        />
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)} disabled={busy}>
            Cancel
          </Button>
          <Button onClick={confirm} disabled={!ready || busy}>
            {busy ? 'Saving' : confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export function DoNotInterviewButton({
  applicationId,
  name,
  onDone,
  compact = false,
}: {
  applicationId: string
  name: string | null
  onDone?: () => void
  compact?: boolean
}) {
  const block = useBlockFromInterviewing()
  const who = name?.trim() || 'this applicant'
  return (
    <ReasonDialog
      trigger={
        <Button variant="outline" size="sm" className={compact ? 'h-7 px-2 text-xs' : undefined}>
          <Ban className="mr-1.5 h-3.5 w-3.5" /> Do not interview again
        </Button>
      }
      title={`Do not allow ${who} to interview again`}
      description="This is permanent. They will not be able to apply again with the same ID number, email or phone. An application still waiting for an interview is closed. Say why; the reason is kept with the block."
      placeholder="For example: rude to the interviewer on the phone"
      confirmLabel="Block from interviewing"
      onConfirm={async (reason) => {
        try {
          const res = (await block.trigger({ application_id: applicationId, reason }).result) as { message?: string } | null
          toast.success(res?.message ?? 'Blocked.')
          onDone?.()
          return true
        } catch (err) {
          toast.error(writeErrorMessage(err))
          return false
        }
      }}
    />
  )
}

export function LiftBlockButton({ blockId, name, onDone }: { blockId: string; name: string | null; onDone?: () => void }) {
  const lift = useLiftApplicantBlock()
  const who = name?.trim() || 'this person'
  return (
    <ReasonDialog
      trigger={
        <Button variant="outline" size="sm" className="h-7 px-2 text-xs">
          <Unlock className="mr-1.5 h-3.5 w-3.5" /> Lift block
        </Button>
      }
      title={`Let ${who} apply again`}
      description="The block stays on record, marked as lifted, with your reason. Their old application is not reopened; they can send a new one."
      placeholder="For example: missed the interview because of a hospital visit, confirmed"
      confirmLabel="Lift block"
      onConfirm={async (reason) => {
        try {
          const res = (await lift.trigger({ block_id: blockId, reason }).result) as { message?: string } | null
          toast.success(res?.message ?? 'Block lifted.')
          onDone?.()
          return true
        } catch (err) {
          toast.error(writeErrorMessage(err))
          return false
        }
      }}
    />
  )
}
